// Professional Yellow & Black Invoice Generator for HomeGo Removals
// Clean, APEX-style design with full company details

const generateInvoiceHTML = (data, invoiceType = 'deposit') => {
  const {
    customer_name = "John Smith",
    customer_email = "",
    customer_phone = "",
    quotation_reference = "HG-00001",
    moving_from = "MK11 4BB",
    moving_to = "MK14 7BB",
    move_date = "15th January 2024",
    total_amount = "485",
    deposit_amount = "100",
    // Custom invoice fields
    service_name = "",
    service_description = "",
    // Bank details
    bank_account_name = "HOMEGO REMOVALS & PACKING LTD",
    bank_sort_code = "23-05-80",
    bank_account_number = "57158107",
  } = data;

  const today = new Date();
  const invoiceDate = today.toLocaleDateString('en-GB', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric' 
  });
  
  // Generate invoice number
  const invoiceNumber = invoiceType === 'deposit' 
    ? `#${quotation_reference.replace('HG-', '')}-DEP` 
    : invoiceType === 'receipt'
    ? `#${quotation_reference.replace('HG-', '')}-REC`
    : `#${quotation_reference.replace('HG-', '')}-BAL`;

  const total = parseFloat(total_amount) || 0;
  const deposit = parseFloat(deposit_amount) || 0;
  const balance = total - deposit;
  
  const isDepositInvoice = invoiceType === 'deposit';
  const isReceiptInvoice = invoiceType === 'receipt';

  const packageDetails = {
    "Economy Move": { 
      name: "Economy Move",
      description: "Loading & unloading, placing boxes in chosen rooms"
    },
    "Comfort Move": { 
      name: "Comfort Move",
      description: "Dismantling & reassembling furniture, Loading & unloading, Placing boxes in correct rooms"
    },
    "Comfort Plus": { 
      name: "Comfort Plus",
      description: "Full packing of all items, Fragile packing, Dismantling & reassembling, Loading & unloading"
    },
    "Premium Move": { 
      name: "Premium Move",
      description: "Complete packing, Full unpacking, Furniture placement, Debris removal"
    }
  };

  const pkgDefault = packageDetails[service_name] || packageDetails["Comfort Move"];
  // Use custom service name/description if provided
  const pkg = {
    name: service_name || pkgDefault.name,
    description: service_description || pkgDefault.description
  };

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice ${invoiceNumber}</title>
  <style>
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .no-print { display: none !important; }
      @page { margin: 10mm; size: A4; }
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif;
      background: #f5f5f5;
      padding: 20px;
      min-height: 100vh;
    }
    .invoice {
      max-width: 800px;
      margin: 0 auto;
      background: #fff;
      box-shadow: 0 2px 20px rgba(0,0,0,0.1);
    }
    
    /* === YELLOW HEADER === */
    .header {
      background: #FFD700;
      padding: 25px 35px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    .company-info {
      flex: 1;
    }
    .company-name {
      font-size: 28px;
      font-weight: 900;
      color: #000;
      letter-spacing: -1px;
      margin-bottom: 2px;
    }
    .company-tagline {
      font-size: 16px;
      color: #333;
      font-weight: 500;
      margin-bottom: 10px;
    }
    .company-contact {
      font-size: 12px;
      color: #333;
      line-height: 1.6;
    }
    .company-contact strong {
      color: #000;
    }
    .header-right {
      text-align: right;
    }
    .invoice-badge {
      background: #000;
      color: #fff;
      font-size: 14px;
      font-weight: 700;
      padding: 8px 20px;
      letter-spacing: 2px;
      display: inline-block;
      margin-bottom: 12px;
    }
    .invoice-meta {
      font-size: 12px;
      color: #333;
      text-align: right;
      line-height: 1.8;
    }
    .invoice-meta strong {
      color: #000;
    }
    
    /* === MAIN CONTENT === */
    .content {
      padding: 30px 35px;
    }
    
    /* Bill To Section */
    .bill-to-section {
      display: flex;
      justify-content: space-between;
      margin-bottom: 30px;
      gap: 30px;
    }
    .bill-to-box {
      flex: 1;
      background: #f9f9f9;
      border-left: 4px solid #FFD700;
      padding: 15px 20px;
    }
    .bill-to-label {
      font-size: 10px;
      font-weight: 700;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 6px;
    }
    .bill-to-name {
      font-size: 18px;
      font-weight: 700;
      color: #000;
      margin-bottom: 4px;
    }
    .bill-to-details {
      font-size: 13px;
      color: #555;
      line-height: 1.5;
    }
    .move-info-box {
      flex: 1;
      background: #fffef5;
      border-left: 4px solid #D4AF37;
      padding: 15px 20px;
    }
    .move-route {
      font-size: 14px;
      color: #333;
      margin-bottom: 8px;
    }
    .move-route strong {
      color: #000;
      font-size: 16px;
    }
    .arrow {
      color: #D4AF37;
      font-weight: bold;
      margin: 0 8px;
    }
    
    /* Items Table */
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 0;
    }
    .items-table th {
      background: #FFD700;
      color: #000;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
      padding: 10px 12px;
      text-align: left;
    }
    .items-table th:nth-child(2),
    .items-table th:nth-child(3) {
      text-align: right;
    }
    .items-table td {
      padding: 15px 12px;
      border-bottom: 1px solid #eee;
      vertical-align: top;
    }
    .items-table td:nth-child(2),
    .items-table td:nth-child(3) {
      text-align: right;
      white-space: nowrap;
    }
    .item-name {
      font-size: 14px;
      font-weight: 600;
      color: #000;
      margin-bottom: 3px;
    }
    .item-description {
      font-size: 11px;
      color: #666;
      line-height: 1.4;
    }
    .item-price {
      font-size: 14px;
      font-weight: 500;
      color: #333;
    }
    
    /* Total Row */
    .total-row {
      background: #000;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 15px 18px;
      margin-top: 10px;
    }
    .total-label {
      color: #FFD700;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 1px;
    }
    .total-amount {
      color: ${isDepositInvoice ? '#4ade80' : '#FFD700'};
      font-size: 24px;
      font-weight: 700;
    }
    
    /* Thank You Message */
    .thank-you {
      text-align: center;
      padding: 25px 0;
      background: linear-gradient(135deg, #fffef5 0%, #fff 100%);
      border-top: 2px solid #FFD700;
      border-bottom: 2px solid #FFD700;
      margin: 20px 0;
    }
    .thank-you-text {
      font-size: 18px;
      font-weight: 600;
      color: #D4AF37;
      margin-bottom: 5px;
    }
    .thank-you-sub {
      font-size: 13px;
      color: #666;
    }
    
    /* Bank Details */
    .bank-section {
      background: #f8f8f8;
      padding: 20px 25px;
      border-radius: 8px;
      margin-bottom: 20px;
    }
    .bank-title {
      font-size: 12px;
      font-weight: 700;
      color: #000;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 2px solid #FFD700;
    }
    .bank-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 15px;
    }
    .bank-item {
      text-align: center;
    }
    .bank-label {
      font-size: 10px;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
    }
    .bank-value {
      font-size: 14px;
      font-weight: 700;
      color: #000;
    }
    
    /* Footer */
    .footer {
      background: #1a1a1a;
      padding: 20px 35px;
      text-align: center;
    }
    .footer-company {
      font-size: 14px;
      font-weight: 700;
      color: #FFD700;
      margin-bottom: 8px;
    }
    .footer-details {
      font-size: 11px;
      color: #999;
      line-height: 1.8;
    }
    .footer-details span {
      margin: 0 10px;
      color: #555;
    }
    .footer-phone {
      margin-top: 10px;
      font-size: 16px;
      color: #fff;
      font-weight: 600;
    }
    .footer-phone a {
      color: #FFD700;
      text-decoration: none;
    }
    
    /* Print Button */
    .print-btn {
      display: block;
      margin: 25px auto;
      padding: 14px 45px;
      background: #FFD700;
      color: #000;
      border: none;
      font-size: 14px;
      font-weight: 700;
      letter-spacing: 2px;
      cursor: pointer;
      transition: all 0.2s;
    }
    .print-btn:hover {
      background: #e6c200;
      transform: translateY(-1px);
    }
  </style>
</head>
<body>
  <div class="invoice">
    
    <!-- Yellow Header -->
    <div class="header">
      <div class="company-info">
        <div class="company-name">HOMEGO</div>
        <div class="company-tagline">Removals & Packing Ltd</div>
        <div class="company-contact">
          <strong>Tel:</strong> 0330 043 4791<br>
          <strong>Email:</strong> office@homego-removals.com<br>
          <strong>Web:</strong> www.homego-removals.com
        </div>
      </div>
      <div class="header-right">
        <div class="invoice-badge">${isDepositInvoice ? 'DEPOSIT INVOICE' : isReceiptInvoice ? 'DEPOSIT RECEIPT' : 'BALANCE INVOICE'}</div>
        <div class="invoice-meta">
          <strong>Invoice:</strong> ${invoiceNumber}<br>
          <strong>Date:</strong> ${invoiceDate}<br>
          <strong>Due:</strong> ${isDepositInvoice ? 'Upon Receipt' : 'On Moving Day'}<br>
          <strong>Ref:</strong> ${quotation_reference}
        </div>
      </div>
    </div>
    
    <!-- Main Content -->
    <div class="content">
      
      <!-- Bill To & Move Info -->
      <div class="bill-to-section">
        <div class="bill-to-box">
          <div class="bill-to-label">Bill To</div>
          <div class="bill-to-name">${customer_name.toUpperCase()}</div>
          <div class="bill-to-details">
            ${customer_phone ? `Tel: ${customer_phone}<br>` : ''}
            ${customer_email ? `Email: ${customer_email}` : ''}
          </div>
        </div>
        <div class="move-info-box">
          <div class="bill-to-label">Move Details</div>
          <div class="move-route">
            <strong>${moving_from}</strong>
            <span class="arrow">→</span>
            <strong>${moving_to}</strong>
          </div>
          <div class="bill-to-details">
            ${move_date ? `Moving Date: ${move_date}` : ''}
          </div>
        </div>
      </div>
      
      <!-- Items Table -->
      <table class="items-table">
        <thead>
          <tr>
            <th style="width: 60%">Description</th>
            <th style="width: 20%">Unit Price</th>
            <th style="width: 20%">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div class="item-name">${pkg.name}</div>
              <div class="item-description">${pkg.description}</div>
            </td>
            <td class="item-price">£${parseFloat(total_amount).toFixed(2)}</td>
            <td class="item-price">£${parseFloat(total_amount).toFixed(2)}</td>
          </tr>
          ${!isDepositInvoice ? `
          <tr>
            <td>
              <div class="item-name" style="color: #22c55e;">Less: Deposit Received ${isReceiptInvoice ? '✓ PAID' : ''}</div>
              <div class="item-description">Thank you for your deposit payment</div>
            </td>
            <td class="item-price" style="color: #22c55e;">-£${parseFloat(deposit_amount).toFixed(2)}</td>
            <td class="item-price" style="color: #22c55e;">-£${parseFloat(deposit_amount).toFixed(2)}</td>
          </tr>
          ` : ''}
        </tbody>
      </table>
      
      <!-- Total -->
      <div class="total-row">
        <span class="total-label">${isDepositInvoice ? 'DEPOSIT AMOUNT DUE' : isReceiptInvoice ? 'REMAINING BALANCE DUE' : 'BALANCE AMOUNT DUE'}</span>
        <span class="total-amount">£${isDepositInvoice ? parseFloat(deposit_amount).toFixed(2) : balance.toFixed(2)}</span>
      </div>
      
      ${isReceiptInvoice ? `
      <!-- Deposit Received Confirmation -->
      <div style="background: #d1fae5; border: 2px solid #22c55e; border-radius: 6px; padding: 12px 15px; margin-top: 15px; text-align: center;">
        <p style="margin: 0; font-size: 13px; font-weight: 700; color: #065f46;">
          ✓ We have received your deposit of £${parseFloat(deposit_amount).toFixed(2)} — thank you!
        </p>
      </div>
      <div style="background: #fff3cd; border: 2px solid #ffc107; border-radius: 6px; padding: 12px 15px; margin-top: 10px; text-align: center;">
        <p style="margin: 0; font-size: 13px; font-weight: 600; color: #856404;">
          ⚠️ The remaining balance of £${balance.toFixed(2)} is due on moving day — when the team arrives or before leaving the old property.
        </p>
      </div>
      ` : !isDepositInvoice ? `
      <!-- Payment Notice -->
      <div style="background: #fff3cd; border: 2px solid #ffc107; border-radius: 6px; padding: 12px 15px; margin-top: 15px; text-align: center;">
        <p style="margin: 0; font-size: 13px; font-weight: 600; color: #856404;">
          ⚠️ Payment must be made on the job day or before leaving the old property
        </p>
      </div>
      ` : ''}
      
      <!-- Thank You Message -->
      <div class="thank-you">
        <div class="thank-you-text">Thank You for Choosing HomeGo Removals!</div>
        <div class="thank-you-sub">We appreciate your business and look forward to making your move stress-free.</div>
      </div>
      
      <!-- Bank Details -->
      <div class="bank-section">
        <div class="bank-title">Payment Details - Bank Transfer</div>
        <div class="bank-grid">
          <div class="bank-item">
            <div class="bank-label">Account Name</div>
            <div class="bank-value">${bank_account_name}</div>
          </div>
          <div class="bank-item">
            <div class="bank-label">Sort Code</div>
            <div class="bank-value">${bank_sort_code}</div>
          </div>
          <div class="bank-item">
            <div class="bank-label">Account Number</div>
            <div class="bank-value">${bank_account_number}</div>
          </div>
        </div>
      </div>
      
    </div>
    
    <!-- Footer -->
    <div class="footer">
      <div class="footer-company">HOMEGO REMOVALS & PACKING LTD</div>
      <div class="footer-details">
        Company Registration No: 16811153
        <span>|</span>
        UK's Nationwide Premium Moving Company
        <span>|</span>
        Registered in England & Wales
      </div>
      <div class="footer-phone">
        Call us: <a href="tel:03300434791">0330 043 4791</a>
      </div>
    </div>
    
  </div>
  
  <button class="print-btn no-print" onclick="window.print()">PRINT / SAVE AS PDF</button>
  
  <script>
    // Ensure document is ready
    document.addEventListener('DOMContentLoaded', function() {
      var printBtn = document.querySelector('.print-btn');
      if (printBtn) {
        printBtn.onclick = function(e) {
          e.preventDefault();
          e.stopPropagation();
          setTimeout(function() {
            window.print();
          }, 100);
          return false;
        };
      }
    });
    
    // Keyboard shortcut Ctrl+P / Cmd+P
    document.addEventListener('keydown', function(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        window.print();
      }
    });
  </script>
  
</body>
</html>
`;
};

export default generateInvoiceHTML;
